import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@mantine/core'
import ThemeToggle from './ThemeToggle'
import { authClient } from '../lib/auth-client'
import { checkAdminAccess } from '../admin/server-fns'

const navLink =
  'text-sm font-semibold text-slate-600 no-underline transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'

const navLinkActive =
  'text-sm font-semibold text-blue-700 no-underline dark:text-blue-400'

/* Whether to offer the console at all. Starts false so the link is absent on
   the server render and on the first paint — it appears only once Zitadel has
   confirmed the access token is still active, never optimistically. The server
   fn is the single source of truth (it also honours the dev bypass), so the
   session is a re-check trigger here, not the decision. */
function useAdminAccess(sessionUserId: string | undefined) {
  const [allowed, setAllowed] = useState(false)

  useEffect(() => {
    let current = true
    checkAdminAccess()
      .then((access) => {
        if (current) setAllowed(access.allowed)
      })
      .catch(() => {
        if (current) setAllowed(false)
      })
    return () => {
      current = false
    }
  }, [sessionUserId])

  return allowed
}

export default function Header() {
  const { data: session, isPending } = authClient.useSession()
  const showAdmin = useAdminAccess(session?.user?.id)

  const handleLogout = () => {
    authClient.signOut().then(() => {
      window.location.href = '/'
    })
  }

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 px-4 backdrop-blur-lg sm:px-6 dark:border-slate-800 dark:bg-slate-950/85">
      <nav className="mx-auto flex w-full max-w-6xl items-center gap-6 py-3.5">
        <Link
          to="/"
          className="inline-flex items-center gap-2.5 text-slate-900 no-underline dark:text-white"
        >
          <svg
            viewBox="0 0 16 16"
            width="18"
            height="18"
            aria-hidden="true"
            className="flex-shrink-0"
          >
            <rect x="0" y="0" width="7" height="7" rx="1.5" fill="currentColor" />
            <rect x="9" y="0" width="7" height="7" rx="1.5" fill="currentColor" />
            <rect x="0" y="9" width="7" height="7" rx="1.5" fill="currentColor" />
            <rect
              x="9"
              y="9"
              width="7"
              height="7"
              rx="1.5"
              className="fill-blue-700 dark:fill-blue-400"
            />
          </svg>
          <span className="text-base font-bold tracking-tight font-stretch-[112%]">
            Lugstash
          </span>
        </Link>

        <div className="flex items-center gap-5">
          <Link
            to="/"
            className={navLink}
            activeOptions={{ exact: true }}
            activeProps={{ className: navLinkActive }}
          >
            Home
          </Link>
          <Link
            to="/about"
            className={navLink}
            activeProps={{ className: navLinkActive }}
          >
            About
          </Link>
          {showAdmin && (
            <Link
              to="/admin"
              className={navLink}
              activeProps={{ className: navLinkActive }}
            >
              Admin
            </Link>
          )}
        </div>

        <div className="ml-auto flex items-center gap-3">
          {isPending ? null : session ? (
            <>
              <span className="hidden max-w-40 truncate font-mono text-xs text-slate-500 sm:inline dark:text-slate-400">
                {session.user?.name ?? session.user?.email ?? ''}
              </span>
              <Button variant="default" size="sm" onClick={handleLogout}>
                Sign out
              </Button>
            </>
          ) : (
            <Button component={Link} to="/login" variant="default" size="sm">
              Sign in
            </Button>
          )}
          <ThemeToggle />
        </div>
      </nav>
    </header>
  )
}
